import { Node, Edge } from "@xyflow/react";

export interface ValidationError {
  nodeId: string;
  nodeName: string;
  nodeType: string;
  errors: string[];
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

/**
 * Helper function to check if a node is connected to a specific node type
 */
const isConnectedToNodeType = (
  node: Node<any>,
  edges: Edge[],
  allNodes: Node<any>[],
  targetNodeType: string
): boolean => {
  const incomingEdges = edges.filter((edge) => edge.target === node.id);

  return incomingEdges.some((edge) => {
    const sourceNode = allNodes.find((n) => n.id === edge.source);
    return sourceNode?.type === targetNodeType;
  });
};

/**
 * Helper function to check if a node has a specific node type in its ancestry (recursive)
 */
const hasAncestorNodeType = (
  nodeId: string,
  edges: Edge[],
  allNodes: Node<any>[],
  targetNodeType: string,
  visited: Set<string> = new Set()
): boolean => {
  if (visited.has(nodeId)) return false;
  visited.add(nodeId);

  const incomingEdges = edges.filter((edge) => edge.target === nodeId);

  for (const edge of incomingEdges) {
    const sourceNode = allNodes.find((n) => n.id === edge.source);
    if (sourceNode?.type === targetNodeType) {
      return true;
    }
    // Recursively check ancestors
    if (hasAncestorNodeType(edge.source, edges, allNodes, targetNodeType, visited)) {
      return true;
    }
  }

  return false;
};

export const validateNodeRequiredFields = (
  node: Node<any>,
  edges?: Edge[],
  allNodes?: Node<any>[]
): string[] => {
  const errors: string[] = [];
  const nodeType = node.type;
  const data = node.data;

  switch (nodeType) {
    case "FILE-UPLOAD":
      if (!data.name || !data.key || !data.url) {
        errors.push("File upload is required");
      }
      break;

    case "DOCUMENT-STORE":
      if (!data?.document_store_id) {
        errors.push("DocIQ selection is required");
      }
      break;

    case "FILE-OCR":
      if (!data.file_processing_type) {
        errors.push("Processing type is required");
      }



      break;

    case "FILE-PARSE":
      // Check if template is selected
      if (!data.schema_id) {
        errors.push("Template selection is required");
      }

      // Dynamic validation based on connected node type
      if (edges && allNodes) {
        // Check for DOCUMENT-STORE in ancestry (not just direct connection)
        const isConnectedToDocStore = hasAncestorNodeType(
          node.id,
          edges,
          allNodes,
          "DOCUMENT-STORE"
        );

        if (isConnectedToDocStore) {
          // DocIQ mode - validate document_store_id and file_ids
          if (!data.document_store_id) {
            errors.push("DocIQ ID mapping is required (drag from DocIQ node)");
          }
          if (!data.file_ids) {
            errors.push("File IDs mapping is required (drag from DocIQ node)");
          }
        } else {
          // Standard mode - validate summary field
          if (!data.summary) {
            errors.push("Summary field mapping is required (drag from OCR node)");
          }
        }
      } else {
        // Fallback if edges/nodes not provided - check both possible configurations
        const hasDocStoreFields = data.document_store_id && data.file_ids;
        const hasSummaryField = data.summary;

        if (!hasDocStoreFields && !hasSummaryField) {
          errors.push("Either summary field OR (document_store_id and file_ids) must be configured");
        }
      }
      break;

    case "compare-data":
      if (!data.source_file_id) {
        errors.push("Source file is required");
      }
      if (!data.target_file_id) {
        errors.push("Target file is required");
      }
      break;

    case "FILE-DATABASE":
      if (!data.connection_string) {
        errors.push("Connection string is required");
      }
      if (!data.database_name) {
        errors.push("Database name is required");
      }
      if (!data.collection_name) {
        errors.push("Collection name is required");
      }
      if (!data.results) {
        errors.push("Input data mapping is required (drag from previous node outputs)");
      }
      break;

    case "LLM":
      if (!data.model_name) {
        errors.push("LLM model is required");
      }
      if (!data.api_key) {
        errors.push("API key is required");
      }
      break;

    case "SUMMARY":
      // Model + API key are resolved server-side from AI settings (like Smart
      // Extraction) — not captured here. Only the prompt is required.
      if (!data.prompt) {
        errors.push("Prompt is required");
      }
      break;

    case "WEB-SCRAPER":
      if (!data.seed_url) {
        errors.push("Seed URL is required");
      }
      break;

    case "DOCXIQ-EXPORT":
      if (!data.document_store_id) {
        errors.push("Document store ID is required");
      }
      if (!data.file_object && !data.discovered_files && !data.scraped_pages && !data.file_url && !data.text_content) {
        errors.push("At least one input (file_object, discovered_files, scraped_pages, file_url, or text_content) is required");
      }
      break;

    case "POSTGRES": {
      const conn = data.connection || {};
      if (!conn.host) errors.push("Database host is required");
      if (!conn.database) errors.push("Database name is required");
      if (!conn.user) errors.push("Database username is required");
      if (!data.operation) errors.push("Operation is required");
      if (!data.table) errors.push("Table is required");
      const op = data.operation;
      if ((op === "INSERT" || op === "UPDATE" || op === "UPSERT") && (!data.values || Object.keys(data.values).length === 0)) {
        errors.push("At least one field (column → value) is required");
      }
      if (op === "UPSERT" && (!data.conflict_columns || data.conflict_columns.length === 0)) {
        errors.push("Conflict column(s) are required for Upsert");
      }
      if ((op === "UPDATE" || op === "DELETE") && (!data.where || data.where.length === 0)) {
        errors.push(`At least one WHERE condition is required for ${op === "UPDATE" ? "Update" : "Delete"}`);
      }
      break;
    }

    case "WEBHOOK":
      if (!data.webhook_url) {
        errors.push("Webhook URL is required");
      }
      if (!data.method) {
        errors.push("Method is required");
      }
      if (!data.results) {
        errors.push("Results mapping is required");
      }
      break;

    case "HTTP":
      if (!data.params && !data.url_name) {
        errors.push("Endpoint path is required");
      }
      break;

    case "ONEDRIVEUPLOAD":
      if (!data.inputs?.filename) {
        errors.push("Filename mapping is required (drag from previous node)");
      }
      if (!data.inputs?.buffer) {
        errors.push("Buffer mapping is required (drag from previous node)");
      }
      if (!data.inputs?.folder_id) {
        errors.push("Folder selection is required");
      }
      break;

    case "SMART-PARSE":
      // Validation disabled as per user request
      // We skip checking for document_store_id, file_id, summary, content, and schema
      break;

    case "EMAIL-OCR":
      if (!data.file_context) {
        errors.push("Email content source is required");
      }
      break;

    case "EXCEL":
      if (!data.data) {
        errors.push("Data source is required (e.g. {{ForEach.outputs}})");
      }
      if (!data.file_name) {
        errors.push("Output file name is required");
      }
      if (!data.header_mapping || Object.keys(data.header_mapping).length === 0) {
        errors.push("At least one column mapping is required");
      }
      break;

    case "WORD":
      if (!data.file_name) {
        errors.push("Output file name is required");
      }
      if (!data.content && !data.summary) {
        errors.push("At least one content source (Content or Summary) is required");
      }
      if (!data.prompt) {
        errors.push("Generation prompt is required");
      }
      break;

    case "CODE":
      if (!data.code) {
        errors.push("Code is required");
      }
      if (!data.language) {
        errors.push("Language is required");
      }
      break;

    default:
      break;
  }

  return errors;
};

/**
 * Validates if a node has the required incoming connections
 */
export const validateNodeConnections = (
  node: Node<any>,
  edges: Edge[],
  allNodes?: Node<any>[]
): string[] => {
  const errors: string[] = [];
  const nodeType = node.type;

  // Get incoming edges for this node
  const incomingEdges = edges.filter((edge) => edge.target === node.id);

  // Nodes that require incoming connections
  const requiresInput = ["OCR", "FILE-PARSE", "FILE-DATABASE", "WEBHOOK", "ONEDRIVEUPLOAD", "EMAIL-OCR", "EXCEL", "WORD", "CODE"];

  if (requiresInput.includes(nodeType || "")) {
    if (incomingEdges.length === 0) {
      errors.push("This node requires input from a previous node");
    }
  }

  // Specific connection validations
  switch (nodeType) {
    case "DOCUMENT-STORE":
      if (incomingEdges.length > 0) {
        errors.push("DocIQ should not have any incoming connections");
      }
      break;

    case "HTTP":
      if (incomingEdges.length > 0) {
        errors.push("HTTP node should not have any incoming connections (it's triggered externally)");
      }
      break;

    case "FILE-OCR":
      // Should have FILE-UPLOAD as previous node
      const hasUploadConnection = edges.some(
        (edge) =>
          edge.target === node.id &&
          edges.find((e) => e.source === edge.source)
      );
      if (!hasUploadConnection && incomingEdges.length === 0) {
        errors.push("Connect to a File Upload node");
      }
      break;

    case "FILE-PARSE":
      // Can connect to FILE-OCR, DOCUMENT-STORE, or SUMMARY nodes
      if (incomingEdges.length === 0) {
        errors.push("Connect to either an OCR Processing node, DocIQ node, or LLM node");
      } else if (allNodes) {
        // Validate that it's connected to a valid source
        const hasValidConnection = incomingEdges.some((edge) => {
          const sourceNode = allNodes.find((n) => n.id === edge.source);
          return sourceNode?.type === "FILE-OCR" || sourceNode?.type === "DOCUMENT-STORE" || sourceNode?.type === "SUMMARY" || sourceNode?.type === "IF-ELSE" || sourceNode?.type === "FOR-EACH";
        });

        if (!hasValidConnection) {
          errors.push("Must be connected to either FILE-OCR, DOCUMENT-STORE, LLM node, IF-ELSE or For Each node");
        }

        // Check for conflicting connections (both OCR and DocIQ)
        const hasOCRConnection = isConnectedToNodeType(node, edges, allNodes, "FILE-OCR");
        const hasDocStoreConnection = isConnectedToNodeType(node, edges, allNodes, "DOCUMENT-STORE");

        if (hasOCRConnection && hasDocStoreConnection) {
          errors.push("Cannot connect to both OCR and DocIQ nodes simultaneously");
        }
      }
      break;

    case "FILE-DATABASE":
      // Should have at least one previous node with output data
      if (incomingEdges.length === 0) {
        errors.push("Connect to a node that provides data");
      }
      break;

    case "SMART-PARSE":
      if (incomingEdges.length === 0) {
        errors.push("Connect to DocIQ or OCR Processing node");
      }
      break;

    case "EMAIL-OCR":
      if (incomingEdges.length === 0) {
        errors.push("Connect to an email trigger node (Outlook/Gmail)");
      } else if (allNodes) {
        const hasEmailTrigger = incomingEdges.some((edge) => {
          const sourceNode = allNodes.find((n) => n.id === edge.source);
          return sourceNode?.type === "OUTLOOK-CONNECTOR" || sourceNode?.type === "GMAIL_CONNECTOR";
        });
        if (!hasEmailTrigger) {
          errors.push("Must be connected to an Outlook or Gmail trigger node");
        }
      }
      break;

    case "EXCEL":
      if (incomingEdges.length === 0) {
        errors.push("Connect to a node that outputs array data (e.g. For Each, Parsing)");
      }
      break;

    case "WORD":
      if (incomingEdges.length === 0) {
        errors.push("Connect to a source node (e.g. LLM, Parsing)");
      }
      break;

    case "CODE":
      // Code node usually needs input, but maybe not always? 
      // Logic above (requiresInput) already checks for incoming edges.
      // We can add specific advice if needed.
      if (incomingEdges.length === 0) {
        errors.push("Connect to previous nodes to use their data as inputs");
      }
      break;
  }

  return errors;
};

/**
 * Validates if referenced node data exists
 */
export const validateNodeDataReferences = (
  node: Node<any>,
  allNodes: Node<any>[],
  edges?: Edge[]
): string[] => {
  const errors: string[] = [];
  const data = node.data;

  // Special node type mappings (camelCase references to node types)
  const nodeTypeMapping: Record<string, string> = {
    HttpNode: "HTTP",
    ocrNode: "FILE-OCR",
    parseNode: "FILE-PARSE",
    documentStoreNode: "DOCUMENT-STORE",
    fileUploadNode: "FILE-UPLOAD",
    llmNode: "LLM",
    webhookNode: "WEBHOOK",
    mongodbNode: "FILE-DATABASE",
    transformNode: "TRANSFORM",
    OneDriveUploadNode: "ONEDRIVEUPLOAD",
    OneDriveTriggerNode: "ONEDRIVE-CONNECTOR",
    SummarizeNode: "SUMMARY",
    EmailParsingNode: "EMAIL-OCR",
    ExportToExcelNode: "EXCEL",
    WordNode: "WORD",
    CodeNode: "CODE",
  };

  // Fields to exclude from validation (e.g., HTML templates, large text content)
  const excludedFields = [
    "pdfTemplate",
    "templateHtml",
    "html_content",
    "template_content",
    "pdfData", // Exclude preview variables object
    "multiple_files", // Optional field for SummarizeNode
  ];

  // Filter out excluded fields before checking for node references
  const dataFields = Object.entries(data || {})
    .filter(([key]) => !excludedFields.includes(key))
    .map(([, value]) => value);

  const checkValue = (val: any) => {
    if (!val) return;

    if (typeof val === "object") {
      Object.values(val).forEach(checkValue);
      return;
    }

    if (typeof val === "string" && (val.includes(".outputs.") || val.includes(".inputs."))) {
      // Find all occurrences of "NodeName.outputs." or "NodeName.inputs."
      // regex captures the NodeName part
      const matches = Array.from(val.matchAll(/([a-zA-Z0-9_\-]+)\.(outputs|inputs)\./g));

      matches.forEach((match) => {
        const referencedNodeIdentifier = match[1];

        // Check if this is a special node type reference (e.g., "httpNode")
        const mappedNodeType = nodeTypeMapping[referencedNodeIdentifier];

        let referencedNode: Node<any> | undefined;

        if (mappedNodeType) {
          // Special case: check if there's a connected node of this type
          if (edges) {
            const incomingEdges = edges.filter((edge) => edge.target === node.id);
            referencedNode = incomingEdges
              .map((edge) => allNodes.find((n) => n.id === edge.source))
              .find((n) => n?.type === mappedNodeType);
          } else {
            // Fallback: check if any node of this type exists
            referencedNode = allNodes.find((n) => n.type === mappedNodeType);
          }
        } else {
          // Regular case: Check if referenced node exists by ID or by name (factory name)
          // Use case-insensitive matching for node names to handle variations
          referencedNode = allNodes.find(
            (n) =>
              n.id === referencedNodeIdentifier ||
              n.data?.name === referencedNodeIdentifier ||
              n.data?.name?.toLowerCase() ===
              referencedNodeIdentifier.toLowerCase() ||
              n.type?.toLowerCase() === referencedNodeIdentifier.toLowerCase()
          );
        }

        if (!referencedNode) {
          // Enhanced error message with available node names for debugging
          const availableNames = allNodes
            .map((n) => n.data?.name || n.id)
            .join(", ");
          console.warn(
            `⚠️ Node reference validation: "${referencedNodeIdentifier}" not found. Available nodes: [${availableNames}]`
          );
          errors.push(
            `Referenced node "${referencedNodeIdentifier}" not found`
          );
        }
      });
    }
  };

  dataFields.forEach(checkValue);

  return errors;
};

/**
 * Validates the entire workflow
 */
export const validateWorkflow = (
  nodes: Node<any>[],
  edges: Edge[]
): ValidationResult => {
  const errors: ValidationError[] = [];

  if (nodes.length === 0) {
    return {
      isValid: false,
      errors: [
        {
          nodeId: "workflow",
          nodeName: "Workflow",
          nodeType: "workflow",
          errors: ["Workflow must contain at least one node"],
        },
      ],
    };
  }

  // Validate each node
  nodes.forEach((node) => {
    const nodeErrors: string[] = [];

    // Check required fields (pass edges and nodes for dynamic validation)
    const fieldErrors = validateNodeRequiredFields(node, edges, nodes);
    nodeErrors.push(...fieldErrors);

    // Check connections
    const connectionErrors = validateNodeConnections(node, edges, nodes);
    nodeErrors.push(...connectionErrors);

    // Check data references
    const referenceErrors = validateNodeDataReferences(node, nodes, edges);
    nodeErrors.push(...referenceErrors);

    if (nodeErrors.length > 0) {
      errors.push({
        nodeId: node.id,
        nodeName: node.type || "Unknown",
        nodeType: node.type || "unknown",
        errors: nodeErrors,
      });
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Formats validation errors into a human-readable message
 */
export const formatValidationErrors = (
  validationResult: ValidationResult
): string => {
  if (validationResult.isValid) {
    return "";
  }

  let message = "Please fix the following issues before running the workflow:\n\n";

  validationResult.errors.forEach((nodeError) => {
    message += `${nodeError.nodeName} (${nodeError.nodeId}):\n`;
    nodeError.errors.forEach((error) => {
      message += `  • ${error}\n`;
    });
    message += "\n";
  });

  return message;
};

/**
 * Gets validation status for a specific node
 */
export const getNodeValidationStatus = (
  node: Node<any>,
  edges: Edge[],
  allNodes: Node<any>[]
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  errors.push(...validateNodeRequiredFields(node, edges, allNodes));
  errors.push(...validateNodeConnections(node, edges, allNodes));
  errors.push(...validateNodeDataReferences(node, allNodes, edges));

  return {
    isValid: errors.length === 0,
    errors,
  };
};